import { Controller } from '@hotwired/stimulus'
import 'cropperjs'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

toastr.options = {
  closeButton: false,
  progressBar: true,
  positionClass: 'toast-bottom-right',
  timeOut: 3000
}

export default class extends Controller {
  static targets = [
    'video',
    'videoWrapper',
    'cropperWrapper',
    'fileUploadInput',
    'modeToggle',
    'imageInput',
    'cropperSlot',
    'viewerWrapper'
  ]

  connect () {
    this.useWebcam = false
    this.uploadedFile = null
    this.editButton = null

    const savedMode = localStorage.getItem('faceImageMode')

    if (savedMode === 'webcam') {
      // Only use webcam if it's supported and not denied
      navigator.mediaDevices?.getUserMedia?.({ video: true }).then(stream => {
        stream.getTracks().forEach(t => t.stop()) // Clean up
        this.modeToggleTarget.value = 'webcam'
        this.toggleMode({ target: this.modeToggleTarget })
      }).catch(() => {
        localStorage.setItem('faceImageMode', 'file') // fallback
        this.modeToggleTarget.value = 'file'
        this.toggleMode({ target: this.modeToggleTarget })
      })
    }
  }

  toggleMode (event) {
    this.useWebcam = event.target.value === 'webcam'
    localStorage.setItem('faceImageMode', this.useWebcam ? 'webcam' : 'file')

    if (this.useWebcam) {
      this.startWebcam()
      this.fileUploadInputTarget.classList.add('hidden')
      this.videoWrapperTarget.hidden = false
      this.cropperWrapperTarget.hidden = false
      this.clearFileInput()
      this.removeEditButton()
    } else {
      this.stopWebcam()
      this.fileUploadInputTarget.classList.remove('hidden')
      this.videoWrapperTarget.hidden = true
      this.cropperWrapperTarget.hidden = true
      this.clearImageInput()
    }
  }

  startWebcam () {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.handleWebcamFailure('Your browser does not support webcam access.')
      return
    }

    navigator.permissions?.query({ name: 'camera' }).then(permission => {
      if (permission.state === 'denied') {
        this.handleWebcamFailure('Webcam permission denied in browser settings.')
      } else {
        this.attemptWebcam()
      }
    }).catch(() => {
      this.attemptWebcam()
    })
  }

  attemptWebcam () {
    navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    }).then(stream => {
      this.videoTarget.srcObject = stream

      this.videoTarget.onloadedmetadata = () => {
        console.log('🎥 Video resolution:', this.videoTarget.videoWidth, this.videoTarget.videoHeight)
        this.videoTarget.play()
      }
    }).catch(() => {
      this.handleWebcamFailure('Webcam access denied or not available.')
    })
  }

  handleWebcamFailure (message) {
    alert(message)
    this.fileUploadInputTarget.classList.remove('hidden')
    this.videoWrapperTarget.hidden = true
    this.cropperWrapperTarget.hidden = true
    this.modeToggleTarget.value = 'file'
    this.useWebcam = false
  }

  stopWebcam () {
    const stream = this.videoTarget.srcObject
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      this.videoTarget.srcObject = null
    }
  }

  captureFromWebcam () {
    const canvas = document.createElement('canvas')
    canvas.width = this.videoTarget.videoWidth
    canvas.height = this.videoTarget.videoHeight
    canvas.getContext('2d').drawImage(this.videoTarget, 0, 0)

    canvas.toBlob(blob => {
      if (!blob) {
        alert('Failed to capture image from webcam.')
        return
      }

      const url = URL.createObjectURL(blob)
      this.setCropperImage(url)
    }, 'image/jpeg')
  }

  handleFileUpload (event) {
    const file = event.target.files[0]
    if (!file) return

    this.uploadedFile = file
    this.cropperWrapperTarget.hidden = true
    this.clearImageInput()
    this.insertEditButton()
  }

  insertEditButton () {
    this.removeEditButton()

    this.editButton = document.createElement('button')
    this.editButton.type = 'button'
    this.editButton.textContent = 'Edit Image'
    this.editButton.className = 'mt-2 bg-blue-500 text-white px-2 py-1 rounded'
    this.editButton.addEventListener('click', () => this.editUploadedFile())

    this.fileUploadInputTarget.insertAdjacentElement('afterend', this.editButton)
  }

  removeEditButton () {
    if (this.editButton) {
      this.editButton.remove()
      this.editButton = null
    }
  }

  editUploadedFile () {
    if (!this.uploadedFile) return
    const url = URL.createObjectURL(this.uploadedFile)
    this.setCropperImage(url)
  }

  setCropperImage (imageUrl) {
    const img = new Image()
    img.onload = () => {

      this.cropperWrapperTarget.hidden = false
      this.cropperSlotTarget.innerHTML = `
        <div class="flex flex-col items-center space-y-2">
          <cropper-canvas background style="width: 400px; height: 300px;">
            <cropper-image
              id="cropperImage"
              src="${imageUrl}"
              alt="Face"
              initial-center-size="contain"
              rotatable scalable skewable translatable>
            </cropper-image>

            <cropper-shade></cropper-shade>
            <cropper-handle action="select" plain></cropper-handle>

            <cropper-selection id="cropperSelection" initial-coverage="0.5" movable resizable>
              <cropper-grid role="grid" covered></cropper-grid>
              <cropper-crosshair centered></cropper-crosshair>

              <cropper-handle action="move"></cropper-handle>
              <cropper-handle action="n-resize"></cropper-handle>
              <cropper-handle action="e-resize"></cropper-handle>
              <cropper-handle action="s-resize"></cropper-handle>
              <cropper-handle action="w-resize"></cropper-handle>
              <cropper-handle action="ne-resize"></cropper-handle>
              <cropper-handle action="nw-resize"></cropper-handle>
              <cropper-handle action="se-resize"></cropper-handle>
              <cropper-handle action="sw-resize"></cropper-handle>
            </cropper-selection>
          </cropper-canvas>

          <button type="button"
                  data-action="face-image#submitCrop"
                  class="bg-green-600 text-white px-3 py-1 rounded">
            Crop & Set Image
          </button>
        </div>
      `

      this.viewerWrapperTarget.innerHTML = `
        <cropper-viewer
          selection="#cropperSelection"
          style="width: 240px; border: 1px solid #ccc; border-radius: 0.5rem;">
        </cropper-viewer>
        <span class="text-sm text-gray-600">Live Preview</span>
      `
    }
    img.src = imageUrl
  }

  submitCrop () {
    const selection = document.getElementById('cropperSelection')

    if (!selection || typeof selection.$toCanvas !== 'function') {
      alert('Cropper selection not ready.')
      return
    }

    selection.$toCanvas({ width: 450 }).then(canvas => {
      if (!canvas) {
        alert('Failed to generate canvas from crop.')
        return
      }

      const base64 = canvas.toDataURL('image/jpeg')
      this.imageInputTarget.value = base64
      this.clearFileInput()
      this.removeEditButton()
      toastr.success('Face image cropped and set!')
    }).catch(error => {
      console.error('Crop failed:', error)
      toastr.error('An error occurred while cropping.')
    })
  }

  clearFileInput () {
    if (this.hasFileUploadInputTarget) {
      this.fileUploadInputTarget.value = ''
    }
  }

  clearImageInput () {
    this.imageInputTarget.value = ''
  }
}
